import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import AuthMenu from "@/components/auth-menu";
import MenuTabs from "@/components/menu-tabs";
import MobileSearchModal from "@/components/mobile-search-modal";
import SearchForm from "@/components/search-form";
import { Button } from "@/components/ui/button";
import useFeatureFlag, { CLAIM_BUSINESS } from "@/hooks/use-feature-flag";
import usePWA from "@/hooks/use-pwa";
import { cn } from "@/lib/utils";

import BlogMenu from "./blog-menu";
import BusinessMenu from "./business-menu";
import { LogoIcon, SearchIcon } from "./icons";

function LogoLink() {
  const { homeHref } = usePWA();

  return (
    <Link to={homeHref}>
      <LogoIcon className="h-[34px] w-[110px] md:h-9 md:w-29" />
    </Link>
  );
}

export const HeaderStrategyEnum = {
  absolute: "absolute",
  fixed: "fixed",
  static: "static",
} as const;
export type HeaderStrategyEnum =
  (typeof HeaderStrategyEnum)[keyof typeof HeaderStrategyEnum];

export const HeaderVariantEnum = {
  simplified: "simplified",
  standard: "standard",
} as const;
export type HeaderVariantEnum =
  (typeof HeaderVariantEnum)[keyof typeof HeaderVariantEnum];

interface HeaderProps extends React.ComponentProps<"header"> {
  desktopClassName?: string;
  isExplorePage?: boolean;
  isHomepage?: boolean;
  mobileClassName?: string;
  strategy?: keyof typeof HeaderStrategyEnum;
  variant?: keyof typeof HeaderVariantEnum;
  withBackground?: boolean;
}

export default function Header({
  children,
  className,
  desktopClassName,
  isExplorePage = false,
  isHomepage = false,
  mobileClassName,
  strategy = HeaderStrategyEnum.static,
  variant = HeaderVariantEnum.standard,
  withBackground = false,
  style = {},
}: HeaderProps) {
  const [scrollBg, setScrollBg] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const isClaimBusinessEnabled = useFeatureFlag(CLAIM_BUSINESS);

  const handleSearchButton = () => {
    if (variant === HeaderVariantEnum.standard) {
      setIsSearchModalOpen(true);
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrollBg(window.scrollY > 0);

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const headerClasses = useMemo(() => {
    const c: string[] = [];

    if (
      (
        [HeaderStrategyEnum.absolute, HeaderStrategyEnum.fixed] as string[]
      ).includes(strategy)
    ) {
      c.push(strategy);
      c.push("left-0 top-0 z-2");
    }

    if (withBackground || (strategy === HeaderStrategyEnum.fixed && scrollBg)) {
      c.push("bg-top bg-size-[300%] md:bg-size-[100%]");
      style = {
        ...style,
        backgroundColor: "var(--color-midnight-express)",
        backgroundImage: "url(/images/body.png)",
      };
    }

    return c.join(" ");
  }, [scrollBg, strategy, withBackground, style]);

  return (
    <>
      {/* Mobile */}
      <header
        className={cn(
          "flex h-16 w-full items-center justify-between px-6 transition-all md:hidden",
          headerClasses,
          className,
          mobileClassName
        )}
        style={style}
      >
        {children || (
          <>
            <Button
              aria-label="Search"
              className="size-10 bg-transparent opacity-(--opacity) transition-opacity"
              onClick={handleSearchButton}
              size="icon"
              style={
                {
                  "--opacity": variant === HeaderVariantEnum.standard ? 1 : 0,
                } as React.CSSProperties
              }
            >
              <SearchIcon />
            </Button>
            <div className="flex w-full justify-center">
              <LogoLink />
            </div>
          </>
        )}
        <AuthMenu
          className="ml-auto"
          isExplorePage={isExplorePage}
          isHomepage={isHomepage}
        />
      </header>

      {/* Desktop */}
      <header
        className={cn(
          "hidden h-20 w-full items-center justify-start px-6 transition-all md:flex",
          headerClasses,
          className,
          desktopClassName
        )}
        style={style}
      >
        <LogoLink />
        <SearchForm
          className={"ml-[30px] opacity-(--opacity) transition-opacity"}
          size="small"
          style={
            {
              "--opacity": variant === HeaderVariantEnum.standard ? 1 : 0,
            } as React.CSSProperties
          }
          useIconButton
        />
        {isHomepage && (
          <>
            <BlogMenu className="mr-6 ml-auto" />
            {isClaimBusinessEnabled && <BusinessMenu className="mr-6" />}
          </>
        )}
        <MenuTabs
          className={
            isClaimBusinessEnabled && isHomepage ? undefined : "ml-auto"
          }
        />
        <AuthMenu
          className="ml-[25px]"
          isExplorePage={isExplorePage}
          isHomepage={isHomepage}
        />
      </header>
      <MobileSearchModal
        onOpenChange={setIsSearchModalOpen}
        open={isSearchModalOpen}
      />
    </>
  );
}
